import { PublicClientApplication } from "@azure/msal-browser";

export type OAuthProvider = "google" | "microsoft";

export type OAuthPopupErrorReason = "cancelled" | "blocked" | "in_progress" | "admin_consent" | "unavailable";

export class OAuthPopupError extends Error {
  readonly reason: OAuthPopupErrorReason;

  constructor(reason: OAuthPopupErrorReason, message: string = reason) {
    super(message);
    this.name = "OAuthPopupError";
    this.reason = reason;
  }
}

export const getProviderClientId = (provider: OAuthProvider): string =>
  (provider === "google" ? import.meta.env.VITE_GOOGLE_CLIENT_ID : import.meta.env.VITE_MICROSOFT_CLIENT_ID)?.trim() ??
  "";

export const isProviderConfigured = (provider: OAuthProvider): boolean => getProviderClientId(provider) !== "";

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

let googleLoadPromise: Promise<void> | null = null;
let googleLoaded = false;

const getGoogle = () => (window as Window & { google?: typeof google }).google;

export const preloadGoogle = (): Promise<void> => {
  if (googleLoadPromise) return googleLoadPromise;
  if (!isProviderConfigured("google")) {
    return Promise.reject(new OAuthPopupError("unavailable", "Google client id is not configured"));
  }
  if (getGoogle()?.accounts?.oauth2) {
    googleLoaded = true;
    googleLoadPromise = Promise.resolve();
    return googleLoadPromise;
  }
  googleLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GOOGLE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => {
      googleLoaded = true;
      resolve();
    });
    script.addEventListener("error", () =>
      reject(new OAuthPopupError("unavailable", "Google sign-in script failed to load"))
    );
    document.head.appendChild(script);
  });
  return googleLoadPromise;
};

export const isGoogleReady = (): boolean => googleLoaded;

/**
 * Opens the Google account chooser popup. `requestCode()` runs synchronously in the
 * caller's call stack so Safari keeps the user activation from the tap; callers must
 * only invoke this once `isGoogleReady()` is true.
 */
export const getGoogleAuthCode = (): Promise<string> => {
  if (!isGoogleReady() || !isProviderConfigured("google")) {
    return Promise.reject(new OAuthPopupError("unavailable", "Google sign-in is not ready"));
  }
  return new Promise<string>((resolve, reject) => {
    const oauth2 = getGoogle()?.accounts?.oauth2;
    if (!oauth2) {
      reject(new OAuthPopupError("unavailable", "Google sign-in is not ready"));
      return;
    }
    try {
      oauth2
        .initCodeClient({
          client_id: getProviderClientId("google"),
          scope: "openid email profile",
          ux_mode: "popup",
          select_account: true,
          callback: (response) => {
            if (response.code) {
              resolve(response.code);
              return;
            }
            reject(
              new OAuthPopupError(
                response.error === "access_denied" ? "cancelled" : "unavailable",
                response.error_description || response.error || "Google sign-in failed"
              )
            );
          },
          error_callback: (error) => {
            const reason: OAuthPopupErrorReason =
              error.type === "popup_closed" ? "cancelled" : error.type === "popup_failed_to_open" ? "blocked" : "unavailable";
            reject(new OAuthPopupError(reason, error.message || error.type));
          },
        })
        .requestCode();
    } catch (err) {
      reject(new OAuthPopupError("unavailable", "Google sign-in failed to start"));
    }
  });
};

const MICROSOFT_AUTHORITY = "https://login.microsoftonline.com/common";
const MICROSOFT_SCOPES = ["openid", "profile", "email"];

let msalInstance: PublicClientApplication | null = null;
let msalInitPromise: Promise<void> | null = null;
let msalReady = false;
let msalRedirectPromise: Promise<string | null> | null = null;

const loginRedirectUri = () => `${window.location.origin}/login`;

const CANCELLED_CODES = new Set(["user_cancelled", "access_denied"]);
const ADMIN_CONSENT_CODES = new Set(["consent_required", "interaction_required", "admin_consent_required"]);

const toMicrosoftError = (err: unknown): OAuthPopupError => {
  if (err instanceof OAuthPopupError) return err;
  const e = (err ?? {}) as { name?: string; errorCode?: string; error?: string; errorMessage?: string; message?: string };
  const code = e.errorCode ?? e.error ?? "";
  const message = e.errorMessage ?? e.message ?? "";
  let reason: OAuthPopupErrorReason;
  if (message.includes("AADSTS65001")) reason = "admin_consent";
  else if (CANCELLED_CODES.has(code)) reason = "cancelled";
  else if (code === "interaction_in_progress") reason = "in_progress";
  else if (e.name === "InteractionRequiredAuthError" || ADMIN_CONSENT_CODES.has(code)) reason = "admin_consent";
  else reason = "unavailable";
  return new OAuthPopupError(reason, message || code || "Microsoft sign-in failed");
};

export const preloadMicrosoft = (): Promise<void> => {
  if (msalInitPromise) return msalInitPromise;
  if (!isProviderConfigured("microsoft")) {
    return Promise.reject(new OAuthPopupError("unavailable", "Microsoft client id is not configured"));
  }
  msalInitPromise = (async () => {
    try {
      const instance = new PublicClientApplication({
        auth: {
          clientId: getProviderClientId("microsoft"),
          authority: MICROSOFT_AUTHORITY,
          redirectUri: loginRedirectUri(),
        },
        cache: { cacheLocation: "sessionStorage" },
      });
      await instance.initialize();
      msalInstance = instance;
      msalReady = true;
    } catch (err) {
      throw new OAuthPopupError("unavailable", "Microsoft sign-in failed to initialize");
    }
  })();
  return msalInitPromise;
};

export const isMicrosoftReady = (): boolean => msalReady;

/** True when the current URL carries an MSAL fragment response (`#code=...` or `#error=...`). */
export const isMicrosoftReturn = (): boolean => /(^#|&)(code|error)=/.test(window.location.hash);

/**
 * Completes a Microsoft redirect sign-in on /login. Resolves the ID token, or null when
 * there is no response to process or the user cancelled. Runs once per page load.
 */
export const consumeMicrosoftRedirect = (): Promise<string | null> => {
  if (msalRedirectPromise) return msalRedirectPromise;
  msalRedirectPromise = (async () => {
    await preloadMicrosoft();
    if (!msalInstance) throw new OAuthPopupError("unavailable", "Microsoft sign-in is not ready");
    try {
      const result = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
      if (!result) return null;
      if (!result.idToken) throw new OAuthPopupError("unavailable", "Microsoft sign-in returned no ID token");
      return result.idToken;
    } catch (err) {
      const mapped = toMicrosoftError(err);
      if (mapped.reason === "cancelled") return null;
      throw mapped;
    }
  })();
  return msalRedirectPromise;
};

/** Sends the tab to Microsoft's sign-in page; the returned promise only settles on failure. */
export const startMicrosoftRedirect = async (): Promise<void> => {
  if (!isMicrosoftReady() || !msalInstance) {
    throw new OAuthPopupError("unavailable", "Microsoft sign-in is not ready");
  }
  try {
    await msalInstance.loginRedirect({
      scopes: MICROSOFT_SCOPES,
      prompt: "select_account",
      redirectUri: loginRedirectUri(),
    });
  } catch (err) {
    throw toMicrosoftError(err);
  }
};

export const __resetForTests = (): void => {
  googleLoadPromise = null;
  googleLoaded = false;
  msalInstance = null;
  msalInitPromise = null;
  msalReady = false;
  msalRedirectPromise = null;
};
